import { useStore } from '@nanostores/react';
import { useCallback, useMemo } from 'react';
import { hydraUiApi } from '@/services/api/hydra-ui-api';
import { accountStore } from '@/services/auth/store';
import { postCatalogEvent } from './event-name';
import type { EventProperties, IEvents } from './types';
import { useIsAnalyticsEnabled } from './use-is-analytics-enabled';

/** A value an analytics property can carry on the wire. */
export type AnalyticsPropertyValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | ReadonlyArray<string | number | boolean | null>;

/** Every catalogued property of the event, each holding a wire-safe value. */
export type AnalyticsProperties<Namespace extends keyof IEvents, E extends keyof IEvents[Namespace]> = {
  [K in EventProperties<Namespace, E>]: AnalyticsPropertyValue;
};

/**
 * Post a typed analytics event to the account-scoped tracking endpoint.
 * `useAnalytics` needs a current workspace, which chrome-less pages such as
 * `/onboarding/*` never have; the account is known as soon as the session is,
 * so those pages report through here. Best-effort: resolves on success or
 * failure so it never blocks or surfaces.
 */
export function trackEventForAccount<
  Namespace extends keyof IEvents,
  E extends keyof IEvents[Namespace] & string,
  Properties extends AnalyticsProperties<Namespace, E>,
>(accountId: string, namespace: Namespace, event: E, properties: Properties): Promise<void> {
  return postCatalogEvent(namespace, event, properties, (body) =>
    hydraUiApi.tracking.createTrackEventForAccount({ params: { path: { account_id: accountId } }, body }),
  );
}

/**
 * `useAnalytics` for pages outside a workspace: same signature, account-scoped
 * endpoint. Reads the account store directly rather than `useAuth()`, which
 * throws before the stores are populated — a page can mount ahead of that.
 */
export function useAccountAnalytics() {
  const account = useStore(accountStore);
  const accountId = account?.id;
  const isEnabled = useIsAnalyticsEnabled();

  const track = useCallback(
    <
      Namespace extends keyof IEvents,
      E extends keyof IEvents[Namespace] & string,
      Properties extends AnalyticsProperties<Namespace, E>,
    >(
      namespace: Namespace,
      event: E,
      properties: Properties,
    ) => {
      if (!isEnabled || accountId == null) {
        return Promise.resolve();
      }
      return trackEventForAccount(String(accountId), namespace, event, properties);
    },
    [isEnabled, accountId],
  );

  return useMemo(() => ({ track, isEnabled }), [track, isEnabled]);
}
